import { useState } from 'react';
import { FORM_ERROR } from 'final-form';
import { toast } from 'react-toastify';

import { createProfile, updateProfile, deleteProfile } from '../api';

/**
 * Converte i dati del form nel formato rules dell'API.
 */
export function buildRulesForApi(formData) {
    const { ruleStrategy, selectedProperties = [] } = formData;

    if (ruleStrategy === 'include_all') {
        return [{
            type: 'include',
            target: 'all properties',
        }];
    }

    if (selectedProperties.length === 0) {
        return [];
    }

    if (ruleStrategy === 'include_specific') {
        return [{
            type: 'include',
            target: 'properties',
            properties: selectedProperties,
        }];
    }

    return [];
}

/**
 * Estrae i valori iniziali del form dal profilo.
 */
export function getProfileInitialValues(profile) {
    if (!profile.id) {
        return {
            name: '',
            enabled: true,
            ruleStrategy: 'include_all',
            selectedProperties: [],
        };
    }

    let ruleStrategy = 'include_all';
    let selectedProperties = [];

    if (profile.rules && profile.rules.length > 0) {
        const rule = profile.rules[0];
        if (rule.type === 'include' && rule.target === 'properties') {
            ruleStrategy = 'include_specific';
            selectedProperties = rule.properties || [];
        }
    }

    return {
        name: profile.name || '',
        enabled: profile.enabled ?? true,
        ruleStrategy,
        selectedProperties,
    };
}

/**
 * Custom hook per la gestione del form profilo (create/update/delete).
 *
 * @param {object} profile - dati del profilo corrente
 * @param {function} i18n - funzione di traduzione da withI18n
 * @param {function} onSuccess - callback eseguita dopo create/update/delete
 */
export function useProfileForm(profile, i18n, onSuccess) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const isCreating = !profile.id;

    const onSubmit = async (data) => {
        setIsSubmitting(true);

        const rules = buildRulesForApi(data);
        const payload = {
            name: data.name,
            rules,
            enabled: data.enabled,
        };

        try {
            if (isCreating) {
                await createProfile(payload);
            } else {
                await updateProfile(profile.id, payload);
            }
        } catch (err) {
            setIsSubmitting(false);

            let errorMessage = i18n._('error:unexpected');

            if (err.response) {
                if (err.response.status === 412) {
                    errorMessage = i18n._('fill:required:fields');
                } else if (err.response.status === 422) {
                    errorMessage = err.response.data?.message || i18n._('validation:error');
                }
            } else if (err.request) {
                errorMessage = i18n._('error:server:generic');
            }

            return { [FORM_ERROR]: errorMessage };
        }

        setIsSubmitting(false);

        const msg = isCreating
            ? i18n._('profiles:created')
            : i18n._('profiles:updated');

        toast(msg, {
            position: 'bottom-right',
            type: toast.TYPE.SUCCESS,
        });

        onSuccess();
    };

    const handleDeleteConfirm = async () => {
        setIsDeleting(true);
        try {
            await deleteProfile(profile.id);
            toast(i18n._('profiles:deleted'), {
                position: 'bottom-right',
                type: toast.TYPE.SUCCESS,
            });
            onSuccess();
        } catch (err) {
            toast(i18n._('error:unexpected'), {
                position: 'bottom-right',
                type: toast.TYPE.ERROR,
            });
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    return {
        isCreating,
        isSubmitting,
        isDeleting,
        showDeleteConfirm,
        setShowDeleteConfirm,
        onSubmit,
        handleDeleteConfirm,
    };
}
